// 基于 ajax-hook 拦截 XHR + localForage 存储实现 3DTiles 请求缓存（indexedDB）
// 使用方式：
//   import { init3DTilesCache } from '@/utils/indexDB';
//   init3DTilesCache(['/danzao/', '/foshan1/', '/guangzhou3dtile/']); //完整前缀

import localforage from 'localforage';
import * as AjaxHook from 'ajax-hook';
const proxy = AjaxHook.proxy;
//@ts-ignore
const _unProxy = AjaxHook.unProxy || AjaxHook.unHook || AjaxHook.restore || null;

//添加标记前缀
const FLAG_PREFIX = 'tiles-cache:';

// 存储各前缀对应的 localforage 实例
const storeMap = new Map<string, LocalForage>();
// 存储前缀对应的名称映射
const prefixNameMap = new Map<string, string>();

let started = false;
let includePrefixes: string[] = [];

interface PrefixAndStore {
  prefix: string;
  store: LocalForage;
}

interface CacheOptions {
  name?: string;
}

interface StartOptions {
  include?: string[];
}

interface CacheResult {
  stop: () => void;
  clear: (prefixList?: string[]) => Promise<void>;
  dropDatabase: (prefixList?: string[]) => Promise<void>;
  storeMap: Map<string, LocalForage>;
}

/**
 * 根据前缀生成数据库名称
 * 例如：'/foshan1/' → 'CesiumTilesDB_foshan1'
 */
function getPrefixKey(prefix: string): string {
  // 移除前后斜杠，保留中间部分
  return prefix.replace(/^\/|\/$/g, '');
}

/**
 * 获取或创建指定前缀的 localforage 实例
 */
function getStoreForPrefix(prefix: string): LocalForage {
  if (!storeMap.has(prefix)) {
    // 优先使用自定义名称，否则使用前缀生成
    const customName = prefixNameMap.get(prefix);
    const dbName = customName || `CesiumTilesDB_${getPrefixKey(prefix)}`;
    const instance = localforage.createInstance({
      name: dbName,
      storeName: 'tiles-cache',
    });
    storeMap.set(prefix, instance);
  }
  return storeMap.get(prefix)!;
}

/**
 * 根据 URL 匹配对应的前缀和 store
 * ( /foshan/ 和 /foshan1/ 是不同的存储,需要完整前缀匹配 )
 * 返回 { prefix, store } 或 null
 */
function matchPrefixAndStore(url: string): PrefixAndStore | null {
  if (!url) return null;
  const matched = includePrefixes.find((p) => url.includes(p));
  if (!matched) return null;
  return {
    prefix: matched,
    store: getStoreForPrefix(matched),
  };
}

//localStorage 快速标记某 URL 是否已缓存,避免无缓存也访问 IndexedDB(快速启动)
function getFlag(url: string): boolean {
  return window.localStorage.getItem(FLAG_PREFIX + url) === '1';
}

// 设置标记
function setFlag(url: string, exists: boolean): void {
  if (exists) window.localStorage.setItem(FLAG_PREFIX + url, '1');
  else window.localStorage.removeItem(FLAG_PREFIX + url);
}

/**
 * 启动缓存拦截
 * @param {Object} options
 * @param {string[]} options.include 需要缓存的 URL 前缀（如 '/danzao/', '/foshan1/'）
 */
function _start({ include = [] }: StartOptions = {}): void {
  includePrefixes = Array.isArray(include) ? include : [];

  // 预先创建所有前缀对应的 store
  includePrefixes.forEach((prefix) => {
    getStoreForPrefix(prefix);
  });

  // 确保只注册一次：若支持取消钩子则先取消
  try {
    _unProxy && _unProxy();
  } catch (_) {
    console.log(_);
  }

  proxy({
    // 请求前：若命中缓存则直接返回
    // Cesium采用 XHR 请求3DTiles资源,劫持浏览器所有 XHR 请求
    onRequest: async (config, handler) => {
      const { url } = config; // 拿到请求 URL
      if (!url) {
        handler.next(config);
        return;
      }
      const match = matchPrefixAndStore(url);
      if (match) {
        const { store } = match;
        try {
          if (getFlag(url)) {
            const data = await store.getItem(url);
            if (data !== null && data !== undefined) {
              handler.resolve({
                config,
                status: 200,
                headers: {},
                response: data, // 可为 ArrayBuffer/Blob/字符串，(缓存中Int8Array/Uint8Array 等共享同一块内存)
              });
              return; // 命中缓存,不继续走网络请求
            }
          }
        } catch (e) {
          // 读取缓存失败则走网络请求
          console.log(e);
        }
      }
      // 未命中缓存，继续走网络请求
      handler.next(config);
    },

    // 响应后：写入缓存并继续向后传递
    onResponse: (response, handler) => {
      try {
        const { config: { url = '' } = {}, status, response: data } = response || {};
        if (!url) {
          handler.next(response);
          return;
        }
        const match = matchPrefixAndStore(url);
        if (match && status === 200) {
          const { store } = match;
          // 异步写入，避免阻塞渲染/回调
          Promise.resolve(store.setItem(url, data))
            .then(() => setFlag(url, true))
            .catch((e) => {
              console.log(e);
            });
        }
      } catch (_) {
        console.log(_);
      }
      // 把响应传给 Cesium 继续处理
      handler.next(response);
    },

    onError: (err, handler) => {
      handler.next(err);
    },
  });

  started = true;
}

// 停止拦截
function stop(): void {
  if (started) {
    try {
      _unProxy && _unProxy();
    } catch (_) {
      console.log(_);
    }
    started = false;
  }
}

/**
 * 清空缓存,保留数据库
 * @param {string[]} prefixList 要清空的前缀列表，不传则清空所有
 */
async function clear(prefixList?: string[]): Promise<void> {
  const targetPrefixes = prefixList && prefixList.length > 0 ? prefixList : includePrefixes;

  for (const prefix of targetPrefixes) {
    const store = storeMap.get(prefix);
    if (!store) continue;

    try {
      const keys = await store.keys();

      for (const key of keys) {
        await store.removeItem(key);
        setFlag(key, false);
      }
    } catch (e) {
      console.log(`清空失败 [${prefix}]:`, e);
    }
  }
}

/**
 * 删除整个数据库
 * @param {string[]} prefixList 要删除的前缀列表，若不传则删除所有
 */
async function dropDatabase(prefixList?: string[]): Promise<void> {
  const targetPrefixes = prefixList && prefixList.length > 0 ? prefixList : includePrefixes;

  for (const prefix of targetPrefixes) {
    const customName = prefixNameMap.get(prefix);
    const dbName = customName || `CesiumTilesDB_${getPrefixKey(prefix)}`;
    try {
      await new Promise<void>((resolve, reject) => {
        const req = indexedDB.deleteDatabase(dbName);
        req.onsuccess = () => {
          console.log(`已删除数据库: ${dbName}`);
          // 清理本地标记
          const keys = Object.keys(localStorage).filter(
            (k) => k.startsWith(FLAG_PREFIX) && k.includes(prefix)
          );
          keys.forEach((k) => localStorage.removeItem(k));
          resolve();
        };
        req.onerror = () => reject(req.error);
      });
      storeMap.delete(prefix);
      prefixNameMap.delete(prefix);
    } catch (e) {
      console.log(`删除数据库失败 [${dbName}]:`, e);
    }
  }
}

// 在应用启动时调用
export function init3DTilesCache(include: string[], options: CacheOptions = {}): CacheResult {
  // 如果传入了 options.name，则为每个 prefix 设置自定义名称
  if (Array.isArray(include)) {
    include.forEach((prefix) => {
      if (options.name) {
        prefixNameMap.set(prefix, options.name);
      }
    });
  }

  _start({ include });
  return { stop, clear, dropDatabase, storeMap };
}
